import { API_BASE_URL } from "@/lib/env";

type StorageUploadKind = "product-image" | "invoice";

type UploadInitResponse = {
  upload_url: string;
  upload_method: "PUT";
  upload_headers: Record<string, string>;
  public_url: string;
};

export class ImageUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ImageUploadError";
  }
}

async function initializeUpload(file: File, kind: StorageUploadKind): Promise<UploadInitResponse> {
  const response = await fetch(`${API_BASE_URL}/storage/uploads`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      kind,
      file_name: file.name,
      content_type: file.type,
      size: file.size,
    }),
  });

  const body = await response.json();

  if (!response.ok) {
    throw new ImageUploadError(body.error?.message || "Failed to initialize file upload");
  }

  return body.data as UploadInitResponse;
}

async function uploadViaSignedUrl(file: File, upload: UploadInitResponse): Promise<string> {
  const response = await fetch(upload.upload_url, {
    method: upload.upload_method,
    headers: upload.upload_headers,
    body: file,
  });

  if (!response.ok) {
    throw new ImageUploadError("Failed to upload file to object storage");
  }

  return upload.public_url;
}

async function uploadManagedFile(file: File, kind: StorageUploadKind): Promise<string> {
  const upload = await initializeUpload(file, kind);
  return uploadViaSignedUrl(file, upload);
}

export async function uploadProductImage(file: File): Promise<string> {
  return uploadManagedFile(file, "product-image");
}

export async function uploadInvoiceAsset(file: File): Promise<string> {
  return uploadManagedFile(file, "invoice");
}

export async function deleteProductImage(imageUrl: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/storage/objects`, {
    method: "DELETE",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ url: imageUrl }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ImageUploadError(body?.error?.message || "Failed to delete image");
  }
}
